// src/components/auth/CodeEntry.tsx
// Enter a 6-digit code. Presentational: the parent supplies what "verify" and
// "resend" do, so the same component serves forgot-password and change-email.
import { useEffect, useState } from 'react';
import { OTPInput } from '@/components/ui/OTPInput';
import { ApiError } from '@/lib/api/client';

type Props = {
  /** Sentence above the boxes, e.g. "We sent a 6-digit code to t***@glowhire.co.za." */
  message: string;
  /** Throw to show an error (an ApiError's message is shown as-is). */
  onVerify: (code: string) => Promise<void>;
  /** Resolve with the new cooldown in seconds. Omit to hide the resend button. */
  onResend?: () => Promise<number>;
  initialCooldown?: number;
  /** Digits in the code (SMS 4, email 6). Defaults to 6. */
  length?: number;
  verifyLabel?: string;
  /** Optional second action, e.g. "Use SMS instead". Resolves with the new cooldown in seconds. */
  alt?: { label: string; run: () => Promise<number> };
};

function messageOf(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}

export function CodeEntry({
  message,
  onVerify,
  onResend,
  initialCooldown = 0,
  length = 6,
  verifyLabel = 'Verify',
  alt,
}: Props) {
  const [code, setCode] = useState('');
  const [inputKey, setInputKey] = useState(0); // remount OTPInput to clear it
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(initialCooldown);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  function clearInput() {
    setCode('');
    setInputKey((k) => k + 1);
  }

  async function verify() {
    setBusy(true);
    setError(null);
    try {
      await onVerify(code);
    } catch (err) {
      setError(messageOf(err));
      clearInput();
    } finally {
      setBusy(false);
    }
  }

  async function resend(action: (() => Promise<number>) | undefined = onResend) {
    if (!action) return;
    setBusy(true);
    setError(null);
    try {
      setCooldown(await action());
      clearInput();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  const ready = code.length === length && !busy;
  const linkStyle = {
    background: 'transparent',
    border: 'none',
    color: 'rgba(var(--fg-rgb),0.45)',
  } as const;

  return (
    <section style={{ width: '100%' }}>
      <p
        className="text-center"
        style={{ color: 'rgba(var(--fg-rgb),0.5)', fontSize: 13, lineHeight: 1.5, marginBottom: 24 }}
      >
        {message}
      </p>

      <OTPInput key={`${inputKey}-${length}`} length={length} onChange={setCode} onComplete={setCode} />

      <button
        type="button"
        onClick={verify}
        disabled={!ready}
        className="btn-pill"
        style={{ width: '100%', marginTop: 28, opacity: ready ? 1 : 0.4, cursor: ready ? 'pointer' : 'not-allowed' }}
      >
        {busy ? 'Working…' : verifyLabel}
      </button>

      {error && (
        <p className="mono-label" role="alert" style={{ marginTop: 10, color: 'var(--color-error)', textAlign: 'center' }}>
          {error}
        </p>
      )}

      <div className="flex items-center justify-center gap-6 mt-4">
        {onResend && (
          <button
            type="button"
            className="mono-label"
            onClick={() => resend()}
            disabled={busy || cooldown > 0}
            style={{ ...linkStyle, cursor: busy || cooldown > 0 ? 'not-allowed' : 'pointer' }}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
          </button>
        )}
        {alt && (
          <button
            type="button"
            className="mono-label"
            onClick={() => resend(alt.run)}
            disabled={busy || cooldown > 0}
            style={{ ...linkStyle, cursor: busy || cooldown > 0 ? 'not-allowed' : 'pointer' }}
          >
            {alt.label}
          </button>
        )}
      </div>
    </section>
  );
}
